import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import { useClubPermissions } from '../hooks/useClubPermissions';
import { SeasonProvider } from '../context/SeasonContext';
import PlannedSessionsTable from '../components/sessions/PlannedSessionsTable';
import {
  createSession,
  updateSession, 
  updateSessionStatus,
  cancelSession,
  uncancelSession,
  deleteSession, 
  startScheduledClubSession,
} from '../services/sessionManagementService';
import Button from '../components/common/Button';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { BackButton } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import SessionTemplateForm from '../components/sessions/SessionTemplateForm';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { Checkbox } from '../components/common/Form';
import { Modal, ModalContent, ModalCloseButton } from '../components/common';
import CollapsibleSection from '../components/common/CollapsibleSection';
import { useClubSessionsHub } from '../hooks/useClubSessionsHub';
import {
  countMissedPlannedSessions,
  getNextPlannedSession,
} from '../components/sessions/hub/sessionHubUtils';
import SessionsPageShell from '../components/sessions/hub/SessionsPageShell';
import ClubLogo from '../components/clubs/ClubLogo';

const SessionManagement = ({ currentUser }) => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get navigation context from state
  const searchParams = new URLSearchParams(location.search);
  const scrollToPastSection = searchParams.get('section') === 'past';
  const startNowQuery = searchParams.get('quick') === '1';
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState(null);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showCancelledSessions, setShowCancelledSessions] = useState(false);
  const [showCreateSessionForm, setShowCreateSessionForm] = useState(false);

  // Use club permissions hook to get club context from URL
  const { 
    club: selectedClub, 
    role: clubRole, 
    loading: clubLoading, 
    hasPermission, 
    error: clubError 
  } = useClubPermissions(clubId, currentUser);

  const {
    upcoming: sessions,
    loading,
    error: hubError,
    refresh: refreshHubSessions,
  } = useClubSessionsHub(clubId, {
    includeCancelled: showCancelledSessions,
    scope: 'plan',
  });

  const missedPlannedCount = useMemo(
    () => countMissedPlannedSessions(sessions),
    [sessions]
  );

  const nextPlannedSessions = useMemo(() => {
    const next = getNextPlannedSession(sessions);
    return next ? [next] : [];
  }, [sessions]);

  const [mutationError, setMutationError] = useState(null);
  const error = mutationError || hubError;

  const isAdmin = currentUser?.role === 'admin' || 
                  currentUser?.role === 'super_admin' || 
                  clubRole === 'host' ||
                  clubRole === 'admin' ||
                  clubRole === 'owner';

  const handleEditSession = async (sessionId, updates) => {
    try {
      await updateSession(sessionId, updates);
      await refreshHubSessions(); // Refresh the list
      setMutationError(null);
    } catch (err) {
      console.error('Error updating session:', err);
      setMutationError(err.message || 'Failed to update session');
    }
  };

  const handleUpdateSessionStatus = async (sessionId, newStatus) => {
    try {
      await updateSessionStatus(sessionId, newStatus);
      await refreshHubSessions(); // Refresh the list
      setMutationError(null);
    } catch (err) {
      console.error('Error updating session status:', err);
      setMutationError(err.message || 'Failed to update session status');
    }
  };

  const handleCancelSession = (sessionId) => {
    setSessionToCancel(sessionId);
    setShowCancelConfirm(true);
  };

  const confirmCancelSession = async () => {
    if (!sessionToCancel) return;

    try {
      await cancelSession(sessionToCancel);
      setShowCancelConfirm(false);
      setSessionToCancel(null);
      setMutationError(null);
      await refreshHubSessions();
    } catch (err) {
      console.error('Error cancelling session:', err);
      setMutationError(err.message || 'Failed to cancel session');
    }
  };

  const handleCloseCancelConfirm = () => {
    setShowCancelConfirm(false);
    setSessionToCancel(null);
  };

  const handleDeleteSession = (sessionOrId) => {
    if (typeof sessionOrId === 'object' && sessionOrId?.id) {
      setSessionToDelete(sessionOrId);
    } else {
      const id = sessionOrId;
      const fromUpcoming = sessions.find((s) => s.id === id);
      setSessionToDelete(fromUpcoming || { id, name: 'this session' });
    }
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete?.id) return;

    try {
      await deleteSession(sessionToDelete.id);
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
      setMutationError(null);
      await refreshHubSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      setMutationError(err.message || 'Failed to delete session');
    }
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  };

  const handleUncancelSession = async (sessionId) => {
    try {
      await uncancelSession(sessionId);
      await refreshHubSessions(); // Refresh the list
      setMutationError(null);
    } catch (err) {
      console.error('Error uncancelling session:', err);
      setMutationError(err.message || 'Failed to uncancel session');
    }
  };

  const handleStartSession = async (sessionId) => {
    try {
      const { session: activeSession } = await startScheduledClubSession(sessionId, currentUser.id);
      navigate(`/club/${clubId}/session/${activeSession.id}`);
    } catch (err) {
      console.error('Error starting session:', err);
      setMutationError(err.message || 'Failed to start session');
    }
  };

  const handleManageSignups = (sessionId) => {
    navigate(`/club/${clubId}/sessions/${sessionId}/signups`, { state: location.state });
  };

  const handleSessionCreated = async (sessionData) => {
    try {
      setMutationError(null);
      
      // Create the session with proper field mapping
      await createSession({
        name: sessionData.name,
        description: sessionData.description,
        club_id: selectedClub.id,
        session_type_id: sessionData.session_type_id,
        game_type_restriction: sessionData.game_type_restriction,
        enable_player_categorisation: sessionData.enable_player_categorisation || false,
        competitive_match_format: sessionData.competitive_match_format || 'doubles',
        max_players: sessionData.max_players,
        number_of_courts: sessionData.number_of_courts,
        price_per_player: sessionData.price_per_player,
        court_booking_cost: sessionData.court_booking_cost,
        location: sessionData.location,
        min_elo_level: sessionData.min_elo_level,
        session_date: sessionData.session_date,
        start_time: sessionData.start_time,
        end_time: sessionData.end_time,
        signup_deadline_hours: sessionData.signup_deadline_hours,
        waitlist_enabled: true,
        waitlist_promotion_type: sessionData.waitlist_promotion_type,
        signup_selection: sessionData.signup_selection,
        is_public: sessionData.is_public,
        session_status: sessionData.session_status,
        currency_code: sessionData.currency_code,
        created_by: currentUser.id,
        session_series_id: sessionData.session_series_id || null,
      });
      
      setShowCreateSessionForm(false);
      await refreshHubSessions();
    } catch (err) {
      console.error('Error creating session:', err);
      setMutationError(err.message || 'Failed to create session');
    }
  };

  const openPlanSetup = (path) => {
    navigate(path, {
      state: { ...location.state, returnToDashboard: false, fromPlanSessions: true },
    });
  };

  const planSetupLinks = (
    <>
      <Button
        variant="link"
        size="small"
        className="px-0 min-h-0"
        onClick={() => openPlanSetup(`/club/${clubId}/sessions/setup`)}
      >
        Block bookings, series, and templates
      </Button>
      <Button
        variant="link"
        size="small"
        className="px-0 min-h-0"
        onClick={() => openPlanSetup(`/club/${clubId}/paste-names`)}
      >
        Spreadsheet names
      </Button>
    </>
  );



  if (scrollToPastSection) {
    return <Navigate to={`/club/${clubId}/sessions/review`} replace state={location.state} />;
  }

  if (startNowQuery) {
    return <Navigate to={`/club/${clubId}/sessions/start?quick=1`} replace state={location.state} />;
  }

  if (!currentUser) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="text-center">
          <Heading level={2} className="mb-4">Please Log In</Heading>
          <Text className="mb-6">You need to be logged in to manage sessions.</Text>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Login
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  // Show loading state while fetching club permissions
  if (clubLoading) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <div className="flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            <Text className="ml-3">Loading club details...</Text>
          </div>
        </Card>
      </SessionsPageShell>
    );
  }

  // Show error if club not found or no permission
  if (clubError || !hasPermission) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="py-12 text-center">
          <div className="mb-4 text-6xl">🚫</div>
          <Heading level={2} className="mb-4">Access Denied</Heading>
          <Text className="mb-6 text-text-secondary">
            {clubError || 'You do not have permission to manage this club'}
          </Text>
          <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
            <Button onClick={() => navigate('/')} variant="primary">
              Go to Homepage
            </Button>
            <Button onClick={() => navigate('/my-clubs')} variant="outline">
              View My Clubs
            </Button>
          </div>
        </Card>
      </SessionsPageShell>
    );
  }

  if (!isAdmin) {
    return (
      <SessionsPageShell>
        <Card variant="default" size="medium" className="text-center">
          <Heading level={2} className="mb-4">Admin Access Required</Heading>
          <Text className="mb-6">You need admin privileges to manage sessions.</Text>
          <Button onClick={() => navigate('/upcoming-sessions')} variant="primary">
            View Upcoming Sessions
          </Button>
        </Card>
      </SessionsPageShell>
    );
  }

  return (
    <SeasonProvider clubId={clubId}>
      <SessionsPageShell>
        <div className="mb-6 sm:mb-8">
          <BackButton
            hideOnDesktop
            className="mb-2"
            onClick={() => {
              navigateBack(navigate, location, clubId, `/club-management/${clubId}`);
            }}
          >
            Back to Management
          </BackButton>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-3">
                {selectedClub ? (
                  <ClubLogo
                    logoImageUrl={selectedClub.logo_image_url}
                    clubName={selectedClub.name}
                    size="sm"
                    className="border border-border-medium shadow-brand-sm"
                  />
                ) : null}
                <Heading level={1} className="min-w-0 truncate text-xl font-bold text-text-primary sm:text-2xl">
                  Plan sessions
                </Heading>
              </div>
              <Text className="mt-1 text-sm text-text-secondary">
                Create and manage upcoming sessions for {selectedClub?.name}
              </Text>
              {selectedClub && isAdmin ? (
                <div className="mt-2 hidden flex-wrap gap-x-4 gap-y-1 sm:flex">
                  {planSetupLinks}
                </div>
              ) : null}
            </div>
            {selectedClub && isAdmin ? (
              <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                <Button
                  onClick={() => setShowCreateSessionForm(true)}
                  variant="primary"
                  className="w-full sm:w-auto"
                >
                  Plan session
                </Button>
                <div className="flex flex-wrap gap-x-4 gap-y-1 sm:hidden">
                  {planSetupLinks}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-6 sm:space-y-8">
      {selectedClub && nextPlannedSessions.length > 0 && (
        <CollapsibleSection
          title="Next session"
          iconType="planning"
          defaultExpanded={true}
        >
          <PlannedSessionsTable
            sessions={nextPlannedSessions}
            clubId={clubId}
            onEditSession={handleEditSession}
            onUpdateSessionStatus={handleUpdateSessionStatus}
            onCancelSession={handleCancelSession}
            onUncancelSession={handleUncancelSession}
            onDeleteSession={handleDeleteSession}
            onManageSignups={handleManageSignups}
            currentUser={currentUser}
          />
        </CollapsibleSection>
      )}

      {/* Planned Sessions Section */}
      {selectedClub && (
        <CollapsibleSection
          title="Planned sessions"
          iconType="planning"
          defaultExpanded={true}
        >
          {/* Toolbar: count, filters, view toggle */}
          {!loading && (
            <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-border-medium">
              <Typography variant="body" className="min-w-0 text-text-secondary">
                {sessions.length > 0 ? (
                  <>
                    <span className="font-semibold text-text-primary">{sessions.length}</span>
                    {' '}planned session{sessions.length !== 1 ? 's' : ''}
                  </>
                ) : (
                  'No planned sessions'
                )}
              </Typography>
              <Checkbox
                label="Show cancelled"
                checked={showCancelledSessions}
                onChange={(e) => setShowCancelledSessions(e.target.checked)}
                className="shrink-0"
              />
            </div>
          )}

          {!loading && missedPlannedCount > 0 && (
            <Card variant="warning" size="small" className="mb-4">
              <Text className="text-sm text-text-primary">
                <span className="font-semibold">
                  {missedPlannedCount} planned session{missedPlannedCount === 1 ? '' : 's'}
                </span>{' '}
                {missedPlannedCount === 1 ? 'has' : 'have'} a past date. Cancel or delete any that
                will not run.
              </Text>
            </Card>
          )}

          {/* Error Message */}
          {error && (
            <Card variant="danger" size="medium" className="mb-6">
              <Typography variant="danger">{error}</Typography>
            </Card>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-16">
              <Typography variant="h4" className="animate-pulse">
                Loading sessions...
              </Typography>
            </div>
          )}

          {/* No Sessions */}
          {!loading && sessions.length === 0 && (
            <Card variant="default" size="medium" className="p-8 text-center">
              <div className="text-4xl mb-4">📅</div>
              <Heading level={3} className="mb-4">No planned sessions</Heading>
              <Text className="mb-6 text-text-secondary">
                There are no planned sessions for {selectedClub.name}.
              </Text>
              <Button 
                onClick={() => setShowCreateSessionForm(true)} 
                variant="primary"
                size="large"
              >
                Create First Session
              </Button>
            </Card>
          )}

          {!loading && sessions.length > 0 && (
            <PlannedSessionsTable
              sessions={sessions}
              clubId={clubId}
              onEditSession={handleEditSession}
              onUpdateSessionStatus={handleUpdateSessionStatus}
              onCancelSession={handleCancelSession}
              onUncancelSession={handleUncancelSession}
              onDeleteSession={handleDeleteSession}
              onStartSession={handleStartSession}
              onManageSignups={handleManageSignups}
              currentUser={currentUser}
            />
          )}
        </CollapsibleSection>
      )}

      {/* No Club Selected */}
      {!selectedClub && !loading && (
        <Card variant="default" size="medium" className="p-8 text-center">
          <div className="mb-4 text-6xl">🏸</div>
          <Heading level={3} className="mb-4">No Club Selected</Heading>
          <Text className="text-text-secondary">Please select a club to manage sessions.</Text>
        </Card>
      )}
        </div>

      {/* Cancel Session Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={handleCloseCancelConfirm}
        onConfirm={confirmCancelSession}
        title="Cancel Session"
        message="Are you sure you want to cancel this session? The session will be marked as cancelled but kept in the system for records."
        confirmText="Cancel Session"
        cancelText="Keep Session"
        isDangerous={true}
      />

      {/* Delete Session Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={confirmDeleteSession}
        title="Delete Session"
        message={
          sessionToDelete?.name
            ? `Are you sure you want to permanently delete "${sessionToDelete.name}"? This will remove the session and all player signups from the system. This action cannot be undone.`
            : 'Are you sure you want to permanently delete this session? This will remove the session and all player signups from the system. This action cannot be undone.'
        }
        confirmText="Delete Session"
        cancelText="Keep Session"
        isDangerous={true}
      />

      {/* Create Session Modal */}
      <Modal isOpen={showCreateSessionForm} onClose={() => setShowCreateSessionForm(false)}>
        <ModalContent size="large">
          <ModalCloseButton onClose={() => setShowCreateSessionForm(false)} />
          <SessionTemplateForm
            onSubmit={handleSessionCreated}
            onCancel={() => setShowCreateSessionForm(false)}
            loading={false}
            error={null}
            clubId={selectedClub?.id}
            isModal={true}
            mode="session"
            creationMode="adhoc"
          />
        </ModalContent>
      </Modal>
      </SessionsPageShell>
    </SeasonProvider>
  );
};

export default SessionManagement;
